import type { PlanTier } from './auth';
import { hasRoomService } from './plans';
import { meetsMinPlan } from './plans';

/**
 * The three QR codes a venue can put in front of a guest.
 *
 * ONE vocabulary, read by the config screen, the banner editor and the guest app. Before
 * this there were two overlapping ideas — the dashboard's three QR tabs and the guest
 * app's two-value `GuestEntrance` — and nothing tied them together, so "where does this
 * show" could not be asked in a way all three agreed on.
 *
 * These are QR codes, not order modes. A guest who scans a table QR and then switches to
 * takeaway is still at a table; the QR they scanned is a fact about where they physically
 * are, and it does not change as they tap around. `qrTypeFromPath` derives it from the
 * link they opened for exactly that reason.
 */
export type QrType = 'venue' | 'table' | 'room';

export const QR_TYPES: readonly QrType[] = ['venue', 'table', 'room'];

export interface QrTypeInfo {
  id: QrType;
  /** What the owner calls it. */
  label: string;
  /** One line under the label, in the owner's terms. */
  description: string;
}

export const QR_TYPE_INFO: Record<QrType, QrTypeInfo> = {
  venue: {
    id: 'venue',
    label: 'Venue QR',
    description: 'One QR at the door or counter',
  },
  table: {
    id: 'table',
    label: 'Table QRs',
    description: 'A QR for each table',
  },
  room: {
    id: 'room',
    label: 'In-Room QR',
    description: 'One QR for every room',
  },
};

/**
 * The lowest plan each QR code needs.
 *
 * Table QRs follow waiter service, which is what they exist for — the same minimum the
 * service mode itself uses in Venue Profile. The venue QR is available to everyone,
 * including Free, because it is also the public profile link.
 *
 * `room` is absent on purpose: in-room dining is an EXCLUSIVE entitlement, not a rung, so
 * it cannot be expressed as a minimum. See `hasRoomService`, and the long note on
 * ROOM_SERVICE_PLANS explaining why Hotel shares Scale's number.
 */
const QR_TYPE_MIN_PLAN: Record<'venue' | 'table', PlanTier> = {
  venue: 'free',
  table: 'operate',
};

/**
 * Can this venue offer this QR code at all, whatever the owner has ticked?
 *
 * Entitlement only. Whether the owner WANTS it is `enabled_qr_types` (and, for the room,
 * `location_ordering_enabled`) — see `enabledQrTypes`.
 */
export function qrTypeAvailable(
  type: QrType,
  plan: PlanTier,
  activeAddons?: readonly string[] | null,
): boolean {
  if (type === 'room') return hasRoomService(plan, activeAddons);
  return meetsMinPlan(plan, QR_TYPE_MIN_PLAN[type]);
}

/** Why a QR code cannot be offered, for the owner to read. Null when it can. */
export function qrTypeLockReason(
  type: QrType,
  plan: PlanTier,
  activeAddons?: readonly string[] | null,
): string | null {
  if (qrTypeAvailable(type, plan, activeAddons)) return null;
  return type === 'room'
    ? 'In-room dining is on the Hotel plan, or as an add-on.'
    : 'Table QR codes need the Operation plan or higher.';
}

/** The venue fields this module reads. Kept narrow so the guest and dashboard rows both fit. */
export interface QrConfigSource {
  /** Which printable QRs the owner wants. Room is NOT stored here — see below. */
  enabled_qr_types?: string[] | null;
  /**
   * The in-room dining switch, which doubles as "offer the In-Room QR".
   *
   * Deliberately not duplicated into `enabled_qr_types`: this field already decides
   * whether guests can order to a room at all, which is more than printing a QR. Two
   * fields meaning one thing is how a venue ends up saying yes in one place and no in
   * another — the shape that broke the whole Operations save when a hidden field kept
   * posting a value the owner could not see.
   */
  location_ordering_enabled?: boolean | null;
}

/**
 * Which QR codes this venue actually runs: wanted by the owner AND allowed by the plan.
 *
 * The single accessor for that question. Computed rather than stored, so it cannot go
 * stale when a plan changes or an add-on is revoked — a venue that drops off Hotel simply
 * stops running the room QR, with no row to migrate and nothing left saying otherwise.
 *
 * Order follows QR_TYPES, so every list of these reads the same way everywhere.
 */
export function enabledQrTypes(
  venue: QrConfigSource | null | undefined,
  plan: PlanTier,
  activeAddons?: readonly string[] | null,
): QrType[] {
  // A row saved before the column existed has nothing here, and those venues were already
  // offering the venue QR and their table QRs. Absent must mean "as it was", never "none"
  // — the same rule `widgetVisibleAt` follows, for the same reason.
  const wanted = venue?.enabled_qr_types ?? ['venue', 'table'];

  return QR_TYPES.filter((type) => {
    if (!qrTypeAvailable(type, plan, activeAddons)) return false;
    return type === 'room'
      ? venue?.location_ordering_enabled === true
      : wanted.includes(type);
  });
}

/**
 * Which QR code a guest arrived through, from the path they opened.
 *
 * The PATH, not the order mode. The mode is sticky in sessionStorage and changes when the
 * guest picks takeaway, so reading it would move a guest out of the room they are sitting
 * in. The landing page already derives room service this way, with a note saying the same.
 *
 * Anchored past the slug so a venue slugged "room-service-cafe" does not match.
 */
export function qrTypeFromPath(pathname: string, slug: string): QrType {
  const base = `/v/${slug}`;
  if (pathname.startsWith(`${base}/room-service`)) return 'room';
  if (pathname.startsWith(`${base}/table-service`)) return 'table';
  return 'venue';
}

/**
 * The two-value entrance the section switches are keyed on.
 *
 * `whats_new_enabled` / `whats_new_room_service_enabled` split the world in two, and that
 * stays: a venue turns the whole What's New section on or off per place. The three QR
 * codes map onto it — the venue QR and its table QRs are the same place, which is what
 * the original `GuestEntrance` comment says.
 */
export function entranceForQrType(type: QrType): 'main' | 'room_service' {
  return type === 'room' ? 'room_service' : 'main';
}
